import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { DocAssessStatus } from '../../../../core/status/doc-assess.types';
import { IconComponent, IconName } from '../../../../shared/icon/icon';

/** Visual tone for a verdict row. */
export type DocVerdictTone = 'evaluating' | 'ok' | 'warning' | 'problem' | 'neutral';

/**
 * The traffic-light row for ONE document's AI verdict: a status icon (or spinner
 * while evaluating), the file name, the applicant-facing message, and a short
 * status badge — colour-coded green / amber / red.
 *
 * Presentational and stateless: it renders whatever ``status`` / ``message`` it
 * is given. Used both live during upload (inside ``app-doc-drop``) and read-only
 * on submitted applications, so the signal looks identical everywhere. Project
 * trailing controls (e.g. a remove button) via ``<… doc-verdict-trailing>``.
 */
@Component({
  selector: 'app-doc-verdict',
  imports: [MatProgressSpinnerModule, IconComponent],
  templateUrl: './doc-verdict.html',
  styleUrl: './doc-verdict.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocVerdictComponent {
  /** File name to show. */
  readonly fileName = input.required<string>();
  /** The AI verdict status. */
  readonly status = input.required<DocAssessStatus>();
  /** Applicant-facing message under the file (the AI's note). */
  readonly message = input<string>('');
  /**
   * Force the row into an error tone with this message (e.g. an upload that
   * failed before it could be assessed). Overrides ``status`` when set.
   */
  readonly errorText = input<string | null>(null);

  protected readonly tone = computed<DocVerdictTone>(() => {
    if (this.errorText()) return 'problem';
    switch (this.status()) {
      case 'OK':
        return 'ok';
      case 'WARNING':
        return 'warning';
      case 'PROBLEM':
        return 'problem';
      case 'PENDING':
        return 'evaluating';
      default:
        return 'neutral';
    }
  });

  protected readonly icon = computed<IconName>(() => {
    switch (this.tone()) {
      case 'ok':
        return 'check-circle';
      case 'warning':
        return 'alert-triangle';
      case 'problem':
        return 'x-circle';
      default:
        return 'file-text';
    }
  });

  protected readonly statusLabel = computed<string>(() => {
    switch (this.tone()) {
      case 'evaluating':
        return 'Checking…';
      case 'ok':
        return 'Looks good';
      case 'warning':
        return 'Worth a look';
      case 'problem':
        return 'Problem';
      default:
        return 'Not checked';
    }
  });

  protected readonly note = computed<string>(() => this.errorText() ?? this.message());
}
